import { useState } from 'react'

import { LoaderCircle, MessagePopup, Wizard, WizardStep } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import {
  getFormattedPhoneNumber,
  isOnlyDigits,
  isOnlyLetters,
  isStringOfLength,
} from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { LoadingError } from '@/components/LoadingError'
import { useCanManageOnOffboarding } from '@/hooks/access'
import useUser, { useProfile } from '@/hooks/user'
import { SUB_SERVICE_ORDER_STATUS } from '@/modules/subscriptions/constants'
import subscriptionResources from '@/modules/subscriptions/subscription/resources'
import { commonQueries } from '@/resources/queries'
import { APP_ROUTES } from '@/util/app-routes'
import { validateEmail } from '@/util/formatters/text'

import { getIsActivationDateValid } from '../helpers'
import queries from '../queries'
import type { CreateSubscriptionPayload } from '../types'

import NewSubscriptionAuthorization from './NewSubscriptionAuthorization'
import { NewSubscriptionCompanies } from './NewSubscriptionCompanies'
import NewSubscriptionConfirm from './NewSubscriptionConfirm'
import { NewSubscriptionDepartments } from './NewSubscriptionDepartments'
import NewSubscriptionDetails from './NewSubscriptionDetails'
import {
  NewSubscriptionContextProvider,
  useNewSubscriptionContext,
} from './NewSubscriptionForm.context'
import NewSubscriptionPolicies from './NewSubscriptionPolicies'
import NewSubscriptionType from './NewSubscriptionType'
import NewSubscriptionUser from './NewSubscriptionUser'

import './index.styles.scss'

const NewSubscriptionForm = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const user = useUser()
  const profile = useProfile()
  const canManageOnOffboarding = useCanManageOnOffboarding()

  const [isErrorMessageOpen, setIsErrorMessageOpen] = useState(false)

  const { setIsWizardMode } = useAppContext()
  const newSub = useNewSubscriptionContext()
  const [companyId] = newSub.companyId
  const [subType] = newSub.subType
  const [departmentId] = newSub.departmentId
  const [activationDate] = newSub.activationDate
  const [contactNumber, setContactNumber] = newSub.contactNumber
  const [contactNumberCountryId] = newSub.contactNumberCountryId
  const [email] = newSub.email
  const [name] = newSub.name
  const [simNumber] = newSub.simNumber
  const [simCity] = newSub.simCity
  const [simZip] = newSub.simZip
  const [simType] = newSub.simType
  const [authorizationDocumentId] = newSub.authorizationDocumentId
  const [comment] = newSub.comment
  const [devicePolicyId] = newSub.devicePolicyId
  const [telePolicyId] = newSub.telePolicyId
  const [ecomPolicyIds] = newSub.ecomPolicyIds
  const [employeeNumber] = newSub.employeeNumber
  const [simAddress] = newSub.simAddress
  const [allowSubscriptions] = newSub.allowSubscriptions
  const [eid] = newSub.eid

  const isSimDetailsValid =
    Boolean(simNumber && isOnlyDigits(simNumber)) ||
    (Boolean(simAddress) && Boolean(simZip) && Boolean(simCity) && isOnlyLetters(simCity || ''))
  const isSimValid =
    (['business', 'new-number'].includes(subType) || authorizationDocumentId) && simType !== 'eSim'
      ? isSimDetailsValid
      : !simType ||
        simType === 'eSim' ||
        Boolean(simNumber && isOnlyDigits(simNumber) && isStringOfLength(simNumber, 20))

  const carrier = useQuery({
    queryKey: commonQueries.carrierbyid(),
    queryFn: subscriptionResources.getCarrierById,
    cacheTime: 0,
  })

  const isSubDetailsValid = isSimValid && getIsActivationDateValid(activationDate)
  const createSubMutation = useMutation(
    (payload: CreateSubscriptionPayload) => subscriptionResources.postSubscription(payload),
    {
      onSuccess() {
        queryClient.invalidateQueries(queries.subscriptionList(), { refetchType: 'all' })
        navigate(
          `${APP_ROUTES.SUBSCRIPTIONS_NEW_SUBSCRIPTION_LIST}?status=${SUB_SERVICE_ORDER_STATUS.NEW}`
        )
      },
      onError() {
        setIsErrorMessageOpen(true)
      },
    }
  )

  const handleComplete = () => {
    if (!contactNumber) return

    const formattedNumber = getFormattedPhoneNumber({
      value: contactNumber,
      countryId: contactNumberCountryId,
    })

    const payload: CreateSubscriptionPayload = {
      isBusinessSub: subType === 'business' && allowSubscriptions,
      isNewNumber: subType === 'new-number' && allowSubscriptions,
      // if allowSubscriptions is false, then it's always private subscription
      isPrivateNumber: subType === 'private' || !allowSubscriptions,
      contactNumber: formattedNumber,
      contactNumberCountryId,
      activationDate,
      name,
    }

    if (email) payload.email = email
    if (simType === 'new') payload.isNewSim = true
    if (simType === 'existing') payload.isNewSim = false
    if (simType === 'eSim') payload.isEsim = true

    // if simType is not set, then it's new number
    payload.isNewSim = simType !== 'existing' && simType !== 'eSim'

    if (employeeNumber) payload.employeeNumber = String(employeeNumber)
    if (simNumber) payload.simNumber = simNumber
    if (simZip) payload.simZip = String(simZip)
    if (simCity) payload.simCity = simCity
    if (simAddress) payload.simAddress = simAddress
    if (authorizationDocumentId) payload.authorizationDocumentId = authorizationDocumentId
    if (departmentId) payload.departmentId = departmentId
    if (devicePolicyId) payload.devicePolicyId = devicePolicyId
    if (telePolicyId) payload.telePolicyId = telePolicyId
    if (comment) payload.comment = comment
    if (ecomPolicyIds && ecomPolicyIds.length > 0) payload.ecomPolicyIds = ecomPolicyIds
    if (eid) payload.eid = eid
    if (carrier.data) payload.carrierId = carrier.data

    createSubMutation.mutate(payload)
  }

  // This solutions allows for extraction of code common for all steps - the only way to achieve it atm - see Wizard component
  const renderStep = () => {
    switch (subType) {
      case 'non-business':
        return renderNonBusinessSubSteps()
      case 'private':
        return renderPrivateSubSteps()
      case 'new-number':
        return renderNewNumSubSteps()
      case 'business':
        return renderBusinessSubSteps()

      default: {
        throw Error(`This sub type is not supported: ${subType}`)
      }
    }
  }

  const handleOnStepChange = () => {
    if (contactNumber) {
      const formattedNumber = getFormattedPhoneNumber({
        value: contactNumber,
        countryId: contactNumberCountryId,
      })
      if (contactNumber !== formattedNumber) {
        setContactNumber(formattedNumber)
      }
    }
  }

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HOME)} className="new-subscription-form">
      {user && profile && !canManageOnOffboarding ? (
        <LoadingError />
      ) : user && !profile ? (
        <LoaderCircle />
      ) : (
        <>
          <Container.Header>
            <Translation id="New Subscription" />
          </Container.Header>

          <Wizard
            isBigNavigationMarginTop
            skipButtonTextDefault={<Translation id="Skip" />}
            nextButtonTextDefault={<Translation id="Next Step" />}
            previousButtonTextDefault={<Translation id="Previous Step" />}
            isLoading={createSubMutation.isLoading}
            onIsWizardModeSet={setIsWizardMode}
            onComplete={handleComplete}
            onStepChange={handleOnStepChange}
          >
            <WizardStep
              isValid={Boolean(companyId)}
              name={<Translation id="Company" className="capitalize" />}
            >
              <NewSubscriptionCompanies />
            </WizardStep>

            <WizardStep
              isValid={true}
              isHidden={!allowSubscriptions}
              name={<Translation id="Type" />}
            >
              <NewSubscriptionType />
            </WizardStep>

            {renderStep()}

            <WizardStep
              isValid={isSubDetailsValid}
              name={<Translation id="Subscription" />}
              // isHidden={!allowSubscriptions && !allowEsim}
            >
              <NewSubscriptionDetails />
            </WizardStep>

            <WizardStep
              name={<Translation id="Confirm" />}
              isValid={!createSubMutation.isLoading}
              nextButtonText={<Translation id="Complete" />}
            >
              <NewSubscriptionConfirm />
            </WizardStep>
          </Wizard>

          <MessagePopup
            message={<Translation id="Something went wrong when ordering the new subscription." />}
            title={<Translation id="New subscription error" />}
            isOpen={isErrorMessageOpen}
            onClose={() => setIsErrorMessageOpen(false)}
          />
        </>
      )}
    </Container>
  )
}

export default () => (
  <NewSubscriptionContextProvider>
    <NewSubscriptionForm />
  </NewSubscriptionContextProvider>
)

const renderNonBusinessSubSteps = () => {
  const newSub = useNewSubscriptionContext()
  const [departmentId] = newSub.departmentId
  const [contactNumber] = newSub.contactNumber
  const [email] = newSub.email
  const [name] = newSub.name
  const [allowSubscriptions] = newSub.allowSubscriptions
  const [allowEsim] = newSub.allowEsim

  const isUserValid = Boolean(name && contactNumber && validateEmail(email))
  const isDepartmentValid = Boolean(departmentId)

  return (
    <>
      <WizardStep key="user" isValid={isUserValid} name={<Translation id="User" />}>
        <NewSubscriptionUser />
      </WizardStep>

      <WizardStep
        key="department"
        isValid={isDepartmentValid}
        name={<Translation id="Department" />}
      >
        <NewSubscriptionDepartments />
      </WizardStep>

      <WizardStep key="policies" isSkippable name={<Translation id="Policies" />} isValid={true}>
        <NewSubscriptionPolicies />
      </WizardStep>

      <WizardStep
        key="authorization"
        isValid={true}
        name={<Translation id="Authorization" />}
        isSkippable={true}
        isHidden={!allowSubscriptions && !allowEsim}
      >
        <NewSubscriptionAuthorization />
      </WizardStep>
    </>
  )
}

const renderPrivateSubSteps = () => {
  const newSub = useNewSubscriptionContext()
  const [departmentId] = newSub.departmentId
  const [contactNumber] = newSub.contactNumber
  const [email] = newSub.email
  const [name] = newSub.name

  const isUserValid = Boolean(name && contactNumber && validateEmail(email))
  const isDepartmentValid = Boolean(departmentId)

  return (
    <>
      <WizardStep key="user" isValid={isUserValid} name={<Translation id="User" />}>
        <NewSubscriptionUser />
      </WizardStep>

      <WizardStep
        key="department"
        isValid={isDepartmentValid}
        name={<Translation id="Department" />}
      >
        <NewSubscriptionDepartments />
      </WizardStep>

      <WizardStep key="policies" isSkippable name={<Translation id="Policies" />} isValid={true}>
        <NewSubscriptionPolicies />
      </WizardStep>
    </>
  )
}

const renderNewNumSubSteps = () => {
  const newSub = useNewSubscriptionContext()
  const [departmentId] = newSub.departmentId
  const [contactNumber] = newSub.contactNumber
  const [email] = newSub.email
  const [name] = newSub.name

  const isUserValid = Boolean(name && contactNumber && validateEmail(email))
  const isDepartmentValid = Boolean(departmentId)

  return (
    <>
      <WizardStep isValid={isUserValid} name={<Translation id="User" />}>
        <NewSubscriptionUser />
      </WizardStep>
      <WizardStep isValid={isDepartmentValid} name={<Translation id="Department" />}>
        <NewSubscriptionDepartments />
      </WizardStep>
      <WizardStep isSkippable name={<Translation id="Policies" />} isValid={true}>
        <NewSubscriptionPolicies />
      </WizardStep>
    </>
  )
}

const renderBusinessSubSteps = () => {
  const newSub = useNewSubscriptionContext()
  const [departmentId] = newSub.departmentId
  const [contactNumber] = newSub.contactNumber
  const [name] = newSub.name

  const isUserValid = Boolean(name && contactNumber)
  const isDepartmentValid = Boolean(departmentId)

  return (
    <>
      <WizardStep isValid={isUserValid} name={<Translation id="Usage" />}>
        <NewSubscriptionUser />
      </WizardStep>
      <WizardStep isValid={isDepartmentValid} name={<Translation id="Department" />}>
        <NewSubscriptionDepartments />
      </WizardStep>
    </>
  )
}
